import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { WarehouseListParams, WarehouseTicketStatus } from "./WarehouseOutwardTypes"

// UI/filter state for the Warehouse Outward list — the params GET /warehouse_outward/list takes
// (contract §4.2). Ticket data itself lives in the RTK Query cache, not here.

type Option = { id: number; title: string }

export type WarehouseOutwardFilters = {
    search: string
    /** metric-card filter; undefined = All. */
    status?: WarehouseTicketStatus
    // More filters (PRD §6.3). Source warehouse and Raised by are multiselect (contract §4.2).
    source_sites: Option[]
    assigned_to: Option | null
    raised_by: Option[]
    project_category: Option | null
}

type InitialState = {
    filters: WarehouseOutwardFilters
    page: number
    pageSize: number
}

export const EMPTY_FILTERS: WarehouseOutwardFilters = {
    search: "",
    status: undefined,
    source_sites: [],
    assigned_to: null,
    raised_by: [],
    project_category: null,
}

const initialState: InitialState = { filters: EMPTY_FILTERS, page: 1, pageSize: 10 }

const commaList = (options: Option[]) => (options.length ? options.map(o => o.id).join(",") : undefined)

/**
 * The filters as /list query params. The bulk picklist download sends exactly these too, so the
 * PDF always covers the tickets the list shows (contract §4.3). Multiselects go as a comma list.
 */
export const listParams = (f: WarehouseOutwardFilters): Omit<WarehouseListParams, "page" | "per_page"> => ({
    search_key: f.search,
    status: f.status,
    source_site_id: commaList(f.source_sites),
    assigned_to: f.assigned_to?.id,
    raised_by: commaList(f.raised_by),
    project_category: f.project_category?.id,
})

const WarehouseOutwardSlice = createSlice({
    name: "warehouseOutward",
    initialState,
    reducers: {
        /** Any filter change goes back to page 1. */
        patchWarehouseOutwardFilters: (state, action: PayloadAction<Partial<WarehouseOutwardFilters>>) => {
            state.filters = { ...state.filters, ...action.payload }
            state.page = 1
        },
        setPages: (state, action: PayloadAction<number>) => {
            state.page = action.payload
        },
        setPageSize: (state, action: PayloadAction<number>) => {
            state.pageSize = action.payload
            state.page = 1
        },
        resetWarehouseOutwardFilters: () => initialState,
    },
})

export const { patchWarehouseOutwardFilters, setPages, setPageSize, resetWarehouseOutwardFilters } =
    WarehouseOutwardSlice.actions

export default WarehouseOutwardSlice.reducer
